/**
 * Shared DataTable configurations for consistent styling and behavior
 */
window.DataTableConfig = {
  /**
   * Standard DOM layout for most tables
   */
  standardDom: `
    <"row mb-3"
      <"col-sm-6">
    >
    <"row"
      <"col-sm-12"tr>
    >
    <"row"
      <"col-sm-4 pt-2 pl-1"l>
      <"col-sm-4 pt-3 text-center"i>
      <"col-sm-4 d-flex justify-content-end pt-2"p>
    >
  `,

  /**
   * DOM layout for assignment logs table with search positioning
   */
  assignmentLogDom: `
    <'row mt-2'<'col-sm-12 text-start mb-4 dt-top-left' f>>
    <'row'<'col-sm-12'<'table-responsive 'tr>>>
    <'dt-footer-source'lip>
  `,

  /**
   * DOM layout for Product Reports table with buttons
   */
  productReportDom: `
    <"top d-flex justify-content-between"<"length-menu"l><"search-and-buttons d-flex justify-content-end align-items-center gap-2"fB>>rt<"bottom d-flex justify-content-between"ip><"clear">
  `,

  /**
   * Common default options
   */
  defaults: {
    searchDelay: 1000
  },

  /**
   * LRPO-5810 — move the page-length, info and pagination controls OUT of the
   * horizontally-scrolling table wrapper and into `.list-container__footer`,
   * which is a SIBLING of that wrapper and so can never be clipped by it.
   *
   * DataTables lays these controls out inside its own wrapper, which on these
   * pages sits inside `.list-container__table-wrapper` (`overflow-x: auto`).
   * The tables are wider than the card, so anything at the right-hand end was
   * off-screen until the user scrolled sideways — the reported bug.
   *
   * ⚠ ONE IMPLEMENTATION, ON PURPOSE. This existed as two separate private
   * copies (Territories and My Reps) and not at all on the Assignment Log,
   * which is why the ticket came back three times. Call this; do not re-write it.
   *
   * ⚠ MUST BE IDEMPOTENT — it runs on every draw. appendChild MOVES a node
   * rather than copying it, so repeated calls leave exactly one of each.
   *
   * @param {Element|string} root - the card element, or a selector for it
   */
  relocateFooter: function (root) {
    var el = typeof root === 'string' ? document.querySelector(root) : root;
    // ⚠ Accept a jQuery object too. Every previous copy of this code was jQuery
    // and all three call sites sit inside jQuery blocks, so `relocateFooter($el)`
    // is the natural next thing somebody writes — and an unguarded version threw
    // `el.querySelector is not a function`, leaving the footer silently stranded,
    // which is the very failure this ticket is about.
    if (el && typeof el.querySelector !== 'function' && el.length) el = el[0];
    if (!el || typeof el.querySelector !== 'function') return;

    var move = function (sourceSelector, targetSelector) {
      var node = el.querySelector(sourceSelector);
      var target = el.querySelector(targetSelector);
      if (node && target && node.parentNode !== target) target.appendChild(node);
    };

    move('.dt-length', '.table__show-per-page');
    move('.dt-info', '.table__showing-indicator');
    move('.dt-paging', '.table__pagination');

    var select = el.querySelector('.table__show-per-page select');
    if (select) select.classList.add('form-field__select');
  },

  /**
   * Get configuration for standard tables
   * @param {Object} overrides - Additional options to override defaults
   * @returns {Object} DataTable configuration object
   */
  getStandardConfig: function(overrides = {}) {
    return {
      searchDelay: this.defaults.searchDelay,
      dom: this.standardDom,
      ...overrides
    };
  },

  /**
   * Get configuration for assignment logs table
   * @param {Object} overrides - Additional options to override defaults
   * @returns {Object} DataTable configuration object
   */
  getAssignmentLogConfig: function(overrides = {}) {
    return {
      searchDelay: this.defaults.searchDelay,
      dom: this.assignmentLogDom,
      ...overrides
    };
  },

  /**
   * Get configuration for Product Reports table with buttons
   * @param {Object} overrides - Additional options to override defaults
   * @returns {Object} DataTable configuration object
   */
  getProductReportConfig: function(overrides = {}) {
    return {
      searchDelay: 2000,
      dom: this.productReportDom,
      ...overrides
    };
  }
};

// LRPO-5622 — Turbo Drive caches the page DOM on navigation (turbo:before-cache).
// A DataTable whose rows are still in the DOM is cached WITH those rows, so
// navigating back shows STALE rows from a previous context (e.g. the admin Carts
// page showed carts from another event, with the footer reading "0 of 0" while the
// stale rows lingered — the fresh server-side ajax had correctly returned 0). Destroy
// every DataTable on the page before it is cached so the cached snapshot is clean.
//
// Registered ONCE at module load — Turbo keeps the JS context alive across
// navigations, so this does NOT re-add per navigation. Covers every DataTable in the
// app (admin AND exhibitor) plus any table added later, instead of a per-view guard
// that future tables would forget. Guarded so a torn-down table can't throw.
document.addEventListener('turbo:before-cache', function () {
  if (window.$ && window.$.fn && window.$.fn.dataTable) {
    window.$.fn.dataTable.tables().forEach(function (table) {
      try {
        window.$(table).DataTable().clear().destroy();
      } catch (e) {
        // Table already torn down by its own handler — nothing to do.
      }
    });
  }
});
